import { theme } from "@tm/utility/global";
import styled, { keyframes } from "styled-components";
import { deviceType } from "../../utils";

export const Page = styled.div`
  background: ${theme.smokeWhite};
`;

export const Container = styled.div`
  width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  @media ${deviceType.mobile} {
    width: 100%;
  }
`;

export const Hero = styled.div<{
  bgColor?: string;
  hasOverlayBg?: boolean;
}>`
  background: ${(props) =>
    props.hasOverlayBg ? "none" : props.bgColor || theme.neonGreen};
  position: relative;
  padding: ${(props) => (props.hasOverlayBg ? "24px 0 28px" : "18px 0 0")};
  ${(props) => !props.hasOverlayBg && "border-radius: 0 0 16px 16px;"}
  @media ${deviceType.mobile} {
    padding: 0;
  }
`;

export const HeroBgImage = styled.img`
  position: absolute;
  top: -70px;
  left: 0;
  pointer-events: none;
`;

export const HeroContentOverlay = styled.div`
  position: relative;
  z-index: 1;
`;

export const BreadcrumbBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  @media ${deviceType.mobile} {
    gap: 8px;
    flex-wrap: wrap;
  }
`;

export const AccountPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${theme.lightBlack};
  background: transparent;
  cursor: pointer;
`;

export const BreadcrumbText = styled.span<{ isBold?: boolean }>`
  font-size: 14px;
  line-height: 18px;
  color: ${theme.greyText};
  font-weight: ${(props) => (props.isBold ? 700 : 500)};
`;

export const BreadcrumbSep = styled.span`
  font-size: 14px;
  line-height: 18px;
  color: ${theme.lightBlack};
`;

export const HeroShield = styled.div`
  position: absolute;
  right: 40px;
  top: -20px;
  pointer-events: none;
  opacity: 0.8;
  @media ${deviceType.mobile} {
    right: -40px;
    top: -40px;
    opacity: 0.35;
    transform: scale(0.7);
    transform-origin: top right;
  }
`;

export const HeroHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  @media ${deviceType.mobile} {
    gap: 16px;
  }
`;

export const HeroTitleWrap = styled.div`
  padding: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  @media ${deviceType.mobile} {
    padding: 4px 0 0;
    gap: 8px;
  }
`;

export const HeroTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  @media ${deviceType.mobile} {
    gap: 6px;
  }
`;

export const FormattedTextWrapper = styled.span<{ textColor?: string }>`
  b,
  strong {
    color: ${(props) => props.textColor || theme.blackText};
    font-weight: 400;
  }
`;

export const HeroImageWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: flex-start;
  @media ${deviceType.mobile} {
    display: none;
  }
`;

export const Body = styled.div<{ marginTop?: string }>`
  display: flex;
  flex-direction: column;
  padding: 48px 0 40px;
  margin-top: ${(props) => props.marginTop || "-70px"};
  @media ${deviceType.mobile} {
    padding: 16px;
    margin-top: 0;
    gap: 16px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 16px;

  position: relative;
  z-index: 2;
  @media ${deviceType.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const HeroTopSection = styled.div`
  padding: 16px;
  display: flex;
  align-items: center;
  position: relative;
`;

export const BackArrowButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;

  &:focus {
    outline: none;
  }

  &:active {
    opacity: 0.7;
  }
`;

export const HeroTitleSectionPWA = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 0 16px;
`;

export const HeroTitlePWA = styled.div`
  text-align: center;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  text-transform: capitalize;
`;

export const HeroContentPWA = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const Card = styled.div`
  background: ${theme.whiteText};
  border-radius: 16px;
  overflow: hidden;
`;

export const CardCtaDivider = styled.div`
  position: relative;
  width: 100%;
  height: 1px;
  margin: 0;
  /* Give shadow space to render - padding allows shadow to be visible */
  padding-bottom: 2px;
  overflow: visible;

  /* Divider line with shadow - matching Figma Separator layer */
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${theme.lightSilver};
    box-shadow: 0 1px 2px rgba(74, 74, 74, 0.2);
    z-index: 1;
  }
`;

export const CardCtaButtonList = styled.div`
  /* Apply border-radius to last button to match card's bottom corners */
  button:last-child {
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  }
`;

export const BookingInfoCtaList = styled.div`
  margin-top: -16px;
`;

export const CardPadding = styled.div`
  padding: 20px;
  @media ${deviceType.mobile} {
    padding: 16px;
  }
`;

export const CardHeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

export const CollapsedRow = styled.div`
  background: ${theme.whiteText};
  border-radius: 16px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.08);
  @media ${deviceType.mobile} {
    padding: 14px 16px;
    border-radius: 0px;
  }
`;

export const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 12px;
  @media ${deviceType.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const ContactBox = styled.div`
  border: 1px solid ${theme.gainsBoro};
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 120px;
`;

export const ContactBoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const ContactLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ActionLink = styled.button<{ disabled?: boolean }>`
  appearance: none;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${theme.blueText};
  font-weight: 700;
  font-size: 14px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.disabled ? 0.45 : 1)};
`;

export const ContactHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
`;

export const ContactMobileSection = styled.div<{ zIndex?: number }>`
  background: ${theme.whiteText};
  border-bottom: 1px solid ${theme.gainsBoro};
  padding: 16px;
  box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.2);
  position: relative;
  z-index: ${(props) => props.zIndex || 1};
`;

export const ContactMobileSectionHeader = styled.div`
  margin-bottom: 16px;
`;

export const ContactMobileTitle = styled.span`
  font-size: 16px;
  font-weight: 900;
  color: ${theme.blackText};
  font-family: "Lato", sans-serif;
`;

export const ContactMobileContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PolicyStatusCard = styled.div`
  background: ${theme.whiteText};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  @media ${deviceType.mobile} {
    padding: 0;
  }
`;

export const PolicyStatusText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${theme.gainsBoro};
  border-top-color: ${theme.blueText};
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
  margin-top: 2px;
`;

export const InfoStrip = styled.div`
  background: ${theme.lightBlueBg};
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

export const ListBox = styled.div`
  border: 1px solid ${theme.gainsBoro};
  border-radius: 12px;
  overflow: hidden;
  @media ${deviceType.mobile} {
    border: 0;
  }
`;

export const ListRow = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${theme.gainsBoro};
  :last-child {
    border-bottom: none;
  }
  @media ${deviceType.mobile} {
    padding: 8px 0;
  }
`;

export const RowMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

export const SectionHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const SectionHeaderIcon = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;

  img {
    display: block;
    vertical-align: middle;
  }
`;

export const PlanBox = styled.div`
  background: ${theme.blueText2};
  border-radius: 12px;
  padding: 16px;
`;

export const BenefitGrid = styled.div`
  margin-top: 14px;
  background: ${theme.whiteText};
  border-radius: 10px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 0.9fr;
  @media ${deviceType.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const BenefitCell = styled.div`
  padding: 12px 16px;
  border-right: 1px solid ${theme.gainsBoro};
  display: flex;
  align-items: center;
  gap: 10px;
  :last-child {
    border-right: none;
  }
  @media ${deviceType.mobile} {
    border-right: none;
    border-bottom: 1px solid ${theme.gainsBoro};
    :last-child {
      border-bottom: none;
    }
  }
`;

export const BenefitText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const BenefitCta = styled.button`
  width: 100%;
  height: 100%;
  border: 0;
  background: transparent;
  color: ${theme.blueText};
  font-weight: 700;
  font-size: 12px;
  line-height: 16px;
  cursor: pointer;
  padding: 0;
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.gainsBoro};
  margin: 8px 0;
`;

// ── GI PWA Heading ──────────────────────────────────────────────────────────

export const HeadingCard = styled.div<{ gradient?: string }>`
  padding: 16px;
  ${(props) => props.gradient && `background: ${props.gradient};`}
`;

export const PersuasionTitle = styled.div`
  margin-bottom: 5px;
`;

export const HeadingDivider = styled.div`
  margin-top: 12px;
  border-top: 1px dashed #e4e4e4;
`;

export const OrderIdStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
`;

export const CopyAction = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 0 0 12px;
  color: #2276e3;
  font-size: 14px;
  font-weight: 400;
`;
